import { actions as integrationsActions } from '@saucelabs/sl-module-integrations';
import { actions as messagesActions } from '@saucelabs/sl-module-messages';
import { actions as userConcurrencyActions } from '@saucelabs/sl-module-concurrency';
import { actions as userTunnelsActions } from '@saucelabs/sl-module-tunnels';
import { constants as sentryConstants, Sentry } from '@saucelabs/sl-util-sentry';
import { CURRENT_DC } from '@saucelabs/sl-util-config';
import { cookiesHelper } from '@saucelabs/sl-util-helpers';
import { identify } from '@saucelabs/sl-util-segmentio';
import {
  operations as authOperations,
  utils as authUtils,
} from '@saucelabs/sl-container-authentication';
import { getLDClient } from '@saucelabs/sl-util-launchdarkly';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';
import { operations as onboardingOperations } from '@saucelabs/sl-module-onboarding';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';

import * as constants from './constants';
import * as actions from './actions';
import trackEvent from '../tracking';
import { LOCAL_STORAGE_KEY_ACTIVE_TEAM } from '../constants';

import { parseAssignedTeamsErrorResponse } from '@saucelabs/web-ui-team-management/src/utils';

const HAS_VISITED_SITE_PREFIX = 'segment-identify/hasVisitedSite';

const updateLastUsedDataCenter = (payload) => (dispatch) => {
  if (payload?.dcName) {
    // used by ForgeRock AM to find out to which DC user should be redirected
    cookiesHelper.setItem(constants.LAST_USED_DC, payload.dcName);
  }
  dispatch(actions.updateLastUsedDC(payload));
};

const authenticate = async (dispatch, getState) => {
  try {
    await dispatch(authOperations.getAuthenticatedUser({ skipErrorPage: [401] }));

    const { user } = getState();
    const username = user.get('username');

    if (user.isAuthenticated()) {
      Sentry.setUser({ username });
    }

    dispatch(updateLastUsedDataCenter({ dcName: CURRENT_DC }));
    authUtils.setLogoutUrl(user?.organization?.settings?.logout_url);
  } finally {
    await dispatch(actions.initialized());
  }
};

/**
 * Calls identity on Segment and logs user info and LD flags
 * @param {*} dispatch
 * @param {*} user map from the current state
 * @param {*} ldClient launchdarkly client
 */
const identifyUser = async (dispatch, { user, ldClient }) => {
  try {
    // get user from our local state (map) into object form
    const userJson = user.toJS();

    // get flags from LD
    const ldFlags = ldClient.allFlags() || {};
    const enabledFlags = Object.keys(ldFlags).filter((flag) => ldFlags[flag]); // filter for flags that are set to true
    userJson.flags = enabledFlags;

    // update segment identification if flags are enabled or is_first_session is true
    // TODO: remove the storage after 1~2 months
    const HAS_VISITED_SITE = `${HAS_VISITED_SITE_PREFIX}:${user.getUserID()}`;
    const isFirstSession = user.get('is_first_session') && !localStorage.getItem(HAS_VISITED_SITE);

    if (!isFirstSession) {
      userJson.is_first_session = false;
    }

    // send the isFreemium attribute to Segment
    userJson.isFreemiumUser = user.isFreemiumUser();

    // dispatch identify action to segment
    dispatch(identify(userJson));

    if (isFirstSession) {
      localStorage.setItem(HAS_VISITED_SITE, 'true');
      dispatch(trackEvent('First Session Loaded', {
        product_area: 'Growth',
        product_sub_area: 'Application',
        subject_name: window.location.pathname,
        domain: window.location.origin,
        page: window.location.pathname,
      }));
    }
  } catch (error) {
    // Start the app without adding the flags in case of error or connection issue
    Sentry.withScope((scope) => {
      scope.setTag('owner', sentryConstants.TEAMS.GROWTH);
      Sentry.captureException(error);
    });
  }
};

export const initializeOnboarding = async (dispatch, user) => {
  try {
    // fetch the 'onboarding' object
    const ldFlags = (await getLDClient(user, { fetchFromApi: true }))?.allFlags() || {};
    if (ldFlags['growth-continuous-onboarding']) {
      await dispatch(onboardingOperations.initialize());
    }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('owner', sentryConstants.TEAMS.GROWTH);
      Sentry.captureException(error);
    });
  }
};

/**
 * Fetches the cord auth token for the user
 * @param {*} dispatch
 * @param {*} user
 */
export const initializeCord = async (dispatch, user) => {
  try {
    const ldFlags = (await getLDClient(user, { fetchFromApi: true }))?.allFlags() || {};
    if (ldFlags['growth-cord']) {
      await dispatch(actions.getCordToken());
    }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('owner', sentryConstants.TEAMS.GROWTH);
      Sentry.captureException(error);
    });
  }
};

/**
 * Fetches growth attributes for the user
 * @param {*} dispatch
 */
export const initializeGrowthAttributes = async (dispatch) => {
  try {
    await dispatch(actions.getUserAttributes());
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('owner', sentryConstants.TEAMS.GROWTH);
      Sentry.captureException(error);
    });
  }
};

/**
 * Initialize LaunchDarkly client and identify user with LD flags
 * @param {*} dispatch
 * @param {*} user
 */
const initializeLaunchDarklyClient = async (dispatch, user) => {
  try {
    // get the LD client
    const ldClient = await getLDClient(user);

    // send user info to Segment which will contain ld flags
    identifyUser(dispatch, { user, ldClient });
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('owner', sentryConstants.TEAMS.GROWTH);
      Sentry.captureException(error);
    });
  }
};

/*
Tries to fetch current user credentials while displaying loading state
in the whole UI, might be extended with other initial data requests
required for application to start.
*/
export const initialize = () => async (dispatch, getState) => {
  try {
    await dispatch(authOperations.startCheckTimeToLogout());
  } finally {
    await authenticate(dispatch, getState);
  }
};

// Requests not critical for the initial UI load
export const postInitialize = () => (dispatch, getState) => {
  const { user } = getState();

  dispatch(integrationsActions.fetchJiraIntegration());

  // requests needed only for sauce-admin users
  dispatch(messagesActions.getMessages());

  // Growth requests
  initializeLaunchDarklyClient(dispatch, user);
  initializeOnboarding(dispatch, user);
  initializeCord(dispatch, user);
  initializeGrowthAttributes(dispatch);

  // IAM
  localStorage.setItem(LOCAL_STORAGE_KEY_ACTIVE_TEAM, user?.teams[0]?.id);
};

export const startPolling = () => (dispatch) => {
  dispatch(userConcurrencyActions.subscribeConcurrencies());

  if (isSupportedFeature(features.TUNNEL_PROXIES)) {
    dispatch(userTunnelsActions.subscribeTunnels());
  }
};

export const stopPolling = () => (dispatch) => {
  dispatch(userConcurrencyActions.unsubscribeConcurrencies());
  dispatch(userTunnelsActions.unsubscribeTunnels());
};

export const stop = () => (dispatch) => {
  dispatch(actions.cleanup());
};

export const dismissExpiredTrialModal = () => (dispatch) => {
  dispatch(actions.dismissExpiredTrialModal());
};

export const switchActiveTeam = (payload) => (dispatch) => {
  dispatch(actions.switchActiveTeam(payload))
    .then(() => {
      dispatch(notificationActions.notifySuccess('Active team changed'));
      localStorage.setItem(LOCAL_STORAGE_KEY_ACTIVE_TEAM, payload.team);
      // refetch user data on success
      return dispatch(authOperations.getAuthenticatedUser({ skipErrorPage: [401] }));
    })
    .catch((err) => {
      const title = 'Cannot change active team.';
      const description = parseAssignedTeamsErrorResponse(err.response);
      return dispatch(notificationActions.notifyDanger(title, description));
    });
};
